/**
 * Application shell.
 *
 * Microsoft 365 layout: a slim navy command bar, a collapsible left rail, and the
 * content area. On a phone the rail becomes a bottom bar, because approvals happen
 * on phones and a hamburger menu is a worse place for the four things people
 * actually tap (docs/07).
 *
 * Navigation is permission-driven: an item that the user could not use is not shown.
 * The API refuses it anyway — this is about not offering dead ends.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { PERMISSIONS } from '@asp/shared';
import { useSession } from '../lib/session.tsx';
import { initials } from '../lib/format.ts';
import { useAppName } from '../lib/branding.ts';
import { BrandMark } from './BrandMark.tsx';
import { Badge, Button, Select } from './ui.tsx';

interface NavItem {
  to: string;
  label: string;
  shortLabel: string;
  icon: JSX.Element;
  permissions?: string[];
  /** Shown in the mobile bottom bar. Kept to four. */
  primary?: boolean;
}

const Icon = {
  home: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M10 2.5 2.5 8.2V17h5v-4.6h5V17h5V8.2z" />
    </svg>
  ),
  list: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M3 4.5h14V6H3zm0 4.75h14v1.5H3zM3 14h9v1.5H3z" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M8 14.2 4.1 10.3l1.3-1.3L8 11.6l6.6-6.6 1.3 1.3z" />
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M3 5.5A2.5 2.5 0 0 1 5.5 3H16v2H5.5a.5.5 0 0 0 0 1H17v11H3zm10 5.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5z" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M3 17V8h3v9zm5.5 0V3h3v14zM14 17v-6h3v6z" />
    </svg>
  ),
  cog: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M10 6.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7m0 1.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4M8.8 2h2.4l.3 1.9 1.4.6 1.6-1.1 1.7 1.7-1.1 1.6.6 1.4 1.9.3v2.4l-1.9.3-.6 1.4 1.1 1.6-1.7 1.7-1.6-1.1-1.4.6-.3 1.9H8.8l-.3-1.9-1.4-.6-1.6 1.1-1.7-1.7 1.1-1.6-.6-1.4L2.4 11.2V8.8l1.9-.3.6-1.4-1.1-1.6L5.5 3.8l1.6 1.1 1.4-.6z" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M2.5 13.5h2.2v3.8H2.5zm4.4-5h2.2v8.8H6.9zm4.4-4h2.2v12.8h-2.2zm4.4 7h2.2v5.8h-2.2z" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M10 2a5 5 0 0 0-5 5v3.2L3.5 13h13L15 10.2V7a5 5 0 0 0-5-5m0 15.5a2.2 2.2 0 0 0 2.1-1.5H7.9A2.2 2.2 0 0 0 10 17.5" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden>
      <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16m0 1.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13m-.75 2.25v4.56l3.2 1.92.77-1.28-2.47-1.48V5.75z" />
    </svg>
  ),
};

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', shortLabel: 'Home', icon: Icon.home, primary: true },
  { to: '/requests', label: 'Purchase requests', shortLabel: 'Requests', icon: Icon.list, primary: true },
  {
    to: '/approvals',
    label: 'Awaiting my approval',
    shortLabel: 'Approve',
    icon: Icon.check,
    permissions: [PERMISSIONS.REQUEST_APPROVE_SCHOOL, PERMISSIONS.REQUEST_APPROVE_ESCALATED, PERMISSIONS.REQUEST_RECOMMEND],
    primary: true,
  },
  {
    to: '/finance',
    label: 'Finance',
    shortLabel: 'Finance',
    icon: Icon.wallet,
    permissions: [PERMISSIONS.PO_VIEW, PERMISSIONS.INVOICE_VIEW],
    primary: true,
  },
  {
    to: '/orders',
    label: 'Orders and invoices',
    shortLabel: 'Orders',
    icon: Icon.wallet,
    permissions: [PERMISSIONS.PO_VIEW, PERMISSIONS.INVOICE_VIEW, PERMISSIONS.GOODS_RECEIVE],
  },
  {
    to: '/budgets',
    label: 'Budgets',
    shortLabel: 'Budgets',
    icon: Icon.gauge,
    permissions: [
      PERMISSIONS.BUDGET_VIEW_SCHOOL,
      PERMISSIONS.BUDGET_VIEW_ALL,
      PERMISSIONS.BUDGET_VIEW_DEPARTMENT,
      PERMISSIONS.BUDGET_MANAGE,
    ],
  },
  {
    to: '/buying',
    label: 'Group buying',
    shortLabel: 'Buying',
    icon: Icon.chart,
    permissions: [PERMISSIONS.BUYING_COORDINATE],
  },
  {
    to: '/overtime',
    label: 'Overtime',
    shortLabel: 'Overtime',
    icon: Icon.clock,
    permissions: [
      PERMISSIONS.OVERTIME_SUBMIT,
      PERMISSIONS.OVERTIME_VIEW_OWN,
      PERMISSIONS.OVERTIME_VIEW_DEPARTMENT,
      PERMISSIONS.OVERTIME_VIEW_SCHOOL,
      PERMISSIONS.OVERTIME_VIEW_ALL,
      PERMISSIONS.OVERTIME_APPROVE,
      PERMISSIONS.OVERTIME_EXPORT,
    ],
  },
  {
    to: '/reports',
    label: 'Reports',
    shortLabel: 'Reports',
    icon: Icon.chart,
    permissions: [PERMISSIONS.REPORT_VIEW_SCHOOL, PERMISSIONS.REPORT_VIEW_ALL],
  },
  {
    to: '/admin',
    label: 'Administration',
    shortLabel: 'Admin',
    icon: Icon.cog,
    permissions: [PERMISSIONS.ADMIN_USERS, PERMISSIONS.ADMIN_SETTINGS],
  },
];

export function AppShell() {
  const { session, can, activeSchoolId, setActiveSchoolId, signOut } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const appName = useAppName();

  // Close the account menu on navigation, which is what people expect.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  if (!session) return null;

  const visible = NAV.filter((item) => !item.permissions || can(...item.permissions));
  const mobileItems = visible.filter((item) => item.primary).slice(0, 4);

  return (
    <div className="min-h-dvh">
      {/* ---------------------------------------------------------------- */}
      {/* Command bar */}
      {/* ---------------------------------------------------------------- */}
      <header className="sticky top-0 z-30 bg-navy-900 text-white">
        <div className="flex h-12 items-center gap-3 px-3 sm:px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <BrandMark appName={appName} className="size-7" />
            <span className="hidden sm:inline">{appName}</span>
          </Link>

          {/* School switcher. Only shown when there is a genuine choice, so most
              staff never see a control they cannot use. */}
          {session.accessibleSchools.length > 1 && (
            <Select
              aria-label="School"
              value={activeSchoolId ?? ''}
              onChange={(e) => setActiveSchoolId(e.target.value || null)}
              className="ml-1 h-8 max-w-[13rem] border-navy-700 bg-navy-800 text-[13px] text-white focus:border-emerald-400"
            >
              <option value="">All schools</option>
              {session.accessibleSchools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </Select>
          )}

          <div className="ml-auto flex items-center gap-1">
            <Link
              to="/notifications"
              className="relative grid size-9 place-items-center rounded text-navy-100 hover:bg-navy-800"
              aria-label={`Notifications${session.unreadNotifications ? `, ${session.unreadNotifications} unread` : ''}`}
            >
              {Icon.bell}
              {session.unreadNotifications > 0 && (
                <span className="absolute right-1.5 top-1.5 grid min-w-4 place-items-center rounded-full bg-emerald-500 px-1 text-[10px] font-bold leading-4 text-navy-950">
                  {session.unreadNotifications > 9 ? '9+' : session.unreadNotifications}
                </span>
              )}
            </Link>

            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-navy-800"
              >
                <span className="grid size-7 place-items-center rounded-full bg-navy-700 text-[11px] font-semibold">
                  {initials(session.user.fullName)}
                </span>
                <span className="hidden text-[13px] md:inline">{session.user.fullName}</span>
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 w-64 overflow-hidden rounded-md border border-[var(--border-default)] bg-[var(--surface-1)] text-[var(--ink-primary)] shadow-raised"
                >
                  <div className="border-b border-[var(--border-subtle)] px-3 py-2.5">
                    <p className="text-sm font-semibold">{session.user.fullName}</p>
                    <p className="truncate text-[12px] text-[var(--ink-muted)]">{session.user.email}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {session.assignments.slice(0, 3).map((a) => (
                        <Badge key={a.id} tone="info" dot={false}>
                          {a.role_name}
                          {a.school_name ? ` · ${a.school_name}` : ''}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Link
                    to="/account"
                    className="block px-3 py-2 text-[13px] hover:bg-[var(--surface-2)]"
                    role="menuitem"
                  >
                    Your account and devices
                  </Link>
                  <ThemeToggle />
                  <button
                    onClick={() => void signOut()}
                    role="menuitem"
                    className="w-full border-t border-[var(--border-subtle)] px-3 py-2 text-left text-[13px] text-[var(--color-critical)] hover:bg-[var(--surface-2)]"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Acting-under-delegation is stated permanently, not tucked away: someone
            approving on another person's authority should never forget it. */}
        {session.actingOnBehalfOf && (
          <div className="bg-amber-500 px-4 py-1 text-[12px] font-medium text-amber-950">
            You are acting on behalf of another approver. Every action is recorded as such.
          </div>
        )}
      </header>

      <div className="flex">
        {/* -------------------------------------------------------------- */}
        {/* Left rail — desktop */}
        {/* -------------------------------------------------------------- */}
        <nav
          aria-label="Main"
          className="sticky top-12 hidden h-[calc(100dvh-3rem)] w-56 shrink-0 border-r border-[var(--border-subtle)] bg-[var(--surface-1)] p-2 lg:block"
        >
          <ul className="space-y-0.5">
            {visible.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors',
                      isActive
                        ? 'bg-navy-50 text-navy-900 dark:bg-navy-900/50 dark:text-navy-100'
                        : 'text-[var(--ink-secondary)] hover:bg-[var(--surface-3)] hover:text-[var(--ink-primary)]',
                    ].join(' ')
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {can(PERMISSIONS.REQUEST_DRAFT) && (
            <div className="mt-3 border-t border-[var(--border-subtle)] pt-3">
              <Button variant="primary" full size="sm" onClick={() => (window.location.href = '/requests/new')}>
                New request
              </Button>
            </div>
          )}
        </nav>

        {/* -------------------------------------------------------------- */}
        {/* Content */}
        {/* -------------------------------------------------------------- */}
        <main className="min-w-0 flex-1 px-4 pb-24 pt-5 sm:px-6 lg:pb-8">
          <div className="mx-auto max-w-[86rem]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Bottom bar — mobile. Four destinations, thumb-reachable. */}
      {/* ---------------------------------------------------------------- */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--border-subtle)] bg-[var(--surface-1)] pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        <ul className="grid grid-cols-4">
          {mobileItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  [
                    'flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                    isActive ? 'text-navy-900 dark:text-emerald-300' : 'text-[var(--ink-muted)]',
                  ].join(' ')
                }
              >
                {item.icon}
                {item.shortLabel}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * Theme toggle. Writes data-theme on the root, which the CSS is written to let win
 * in both directions over the OS preference.
 */
function ThemeToggle() {
  const [theme, setTheme] = useState<'system' | 'light' | 'dark'>(
    () => (localStorage.getItem('asp.theme') as 'system' | 'light' | 'dark') ?? 'system',
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    localStorage.setItem('asp.theme', theme);
  }, [theme]);

  const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
  const label = { system: 'Match my device', light: 'Light', dark: 'Dark' }[theme];

  return (
    <button
      role="menuitem"
      onClick={() => setTheme(next)}
      className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-[var(--surface-2)]"
    >
      <span>Appearance</span>
      <span className="text-[var(--ink-muted)]">{label}</span>
    </button>
  );
}
